import { defineStore } from 'pinia';

interface SortProperty {
  displayText: string;
  trans: string;
}

// Define the STUDY_PROPERTIES once
export const STUDY_PROPERTIES = {
  /*
    Taken from REQ-11
    - PatientName
    - Sex
    - Date of birth
    - Date of acquisition
    - Ascession number
    - Medical record number
    - Institution name
    */
  PatientName: { displayText: 'Name', trans: 'clientname' },
  PatientID: { displayText: 'Patient ID', trans: 'clientId' },
  AccessionNumber: { displayText: 'Accession Number', trans: 'studyId' },
  PatientSex: { displayText: 'Sex', trans: 'sex' },
  PatientBirthDate: { displayText: 'DOB', trans: 'dob', isDate: true },
  StudyDate: {
    displayText: 'Acquisition Date',
    trans: 'scandate',
    isDate: true,
  },
} as const;

export const STUDY_SEARCH_FILTER_PROPERTIES = [
  'PatientName',
  'PatientID',
  'StudyDate',
  'AccessionNumber',
  'StudyDescription',
  'Modality',
  'InstitutionName',
] as const;

// Define the structure of a Study object
interface Study {
  id: string;
  title: string;
  description: string;
  date: Date;
  [key: string]: any; // You can add more fields as needed
}

// Define the structure of the state
interface StudyState {
  studies: Study[];
  searchTerm: string;
  studySortBy: string;
}

export const useStudiesStore = defineStore('studiesStore', {
  // State object
  state: (): StudyState => ({
    studies: [],
    searchTerm: '',
    studySortBy: 'StudyDate',
  }),

  // Getters
  getters: {
    getStudySortProperty(state): string {
      return state.studySortBy;
    },

    getStudySearchTerm(state) {
      return state.searchTerm;
    },

    getAllPropertyDisplayText() {
      // Extract displayText from all STUDY_PROPERTIES and return as an array of strings
      return Object.values(STUDY_PROPERTIES).map(
        property => property.displayText,
      );
    },
    getActivePropertyDescriptors() {
      return Object.values(STUDY_PROPERTIES).map(
        property => property.displayText,
      );
    },
  },

  // Actions
  actions: {
    setStudySortProperty(property: string) {
      this.studySortBy = property;
    },
    setSearchTerm(term: string) {
      this.searchTerm = term;
    },
    resetSearchTerm() {
      this.searchTerm = '';
    },
    // Add a new study
    addStudy(study: Study) {
      this.studies.push(study);
    },
    // Remove a study by ID
    removeStudy(studyId: string) {
      this.studies = this.studies.filter(study => study.id !== studyId);
    },
    setStudySortAvailablePropertiesByDescriptor(propertyArray: string[]) {
      const updatedSortProperties: Record<string, SortProperty> = {};

      propertyArray.forEach((displayText) => {
        (
          Object.keys(STUDY_PROPERTIES) as Array<keyof typeof STUDY_PROPERTIES>
        ).forEach((key) => {
          if (STUDY_PROPERTIES[key].displayText === displayText) {
            updatedSortProperties[key] = STUDY_PROPERTIES[key];
          }
        });
      });

      // Replace the STUDY_SORT_PROPERTIES with the new updated properties
      // Object.assign(STUDY_SORT_PROPERTIES, updatedSortProperties);
      return updatedSortProperties;
    },
  },
});
